import { Text } from '@airwallex/typography';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, DATE_PRECISION, DATE_STYLE } from '@/utils/formatDate';
import { VStack } from '@airwallex/stacks';
import { Grid } from '@airwallex/grid';
import { StoreContext } from '@/providers/storeProvider';
import { useTabletOrMobile } from '@/hooks/useMediaQuery';
import { Customer } from '../types';
import { getCustomerDisplayName } from '@/utils/customer';
import { informationWrapperStyle, labelStyle, MemoWrapper } from './style';
import i18n from '@/utils/i18n';

const getBillToTitle = (customerSnapshot?: Customer) => {
  const { email, phoneNumber } = customerSnapshot || {};
  const name = getCustomerDisplayName(customerSnapshot);
  if (name) return name;
  if (email) return email;
  if (phoneNumber) return phoneNumber;
  return '-';
};

const getBillToSubTitle = (customerSnapshot?: Customer) => {
  const { email, phoneNumber } = customerSnapshot || {};
  const name = getCustomerDisplayName(customerSnapshot);
  if (!name) return undefined;
  if (email) return email;
  if (phoneNumber) return phoneNumber;
  return undefined;
};
const BillingInfo: React.FC = () => {
  const { t } = useTranslation();
  const { invoiceData } = useContext(StoreContext);
  const { legalEntity, customerSnapshot, invoice } = invoiceData;
  const { name, email } = legalEntity || {};
  const { finalizedAt, memo } = invoice;
  const isTabletOrMobile = useTabletOrMobile();
  const memoLines =
    memo && memo.length > 0
      ? memo.split('\n').map((line, index) => (
          <div key={index} title={line}>
            {line}
          </div>
        ))
      : null;
  return (
    <>
      {!isTabletOrMobile ? (
        <>
          <Grid data-test="billing-info" columns="minmax(180px, 1fr) minmax(180px, 1fr)" gap="4px 12px">
            <VStack style={informationWrapperStyle}>
              <Text variant="body" color="secondary">
                {t('invoice.billFrom')}
              </Text>
              <Text variant="body" style={labelStyle}>
                {name}
              </Text>
              <Text variant="hint">{email}</Text>
            </VStack>
            <VStack style={informationWrapperStyle}>
              <Text variant="body" color="secondary">
                {t('invoice.billTo')}
              </Text>
              <Text variant="body" style={labelStyle}>
                {getBillToTitle(customerSnapshot)}
              </Text>
              <Text variant="hint">{getBillToSubTitle(customerSnapshot)}</Text>
            </VStack>
            <VStack style={informationWrapperStyle}>
              <Text variant="body" color="secondary">
                {t('invoice.invoiceDate')}
              </Text>
              <Text variant="body" style={labelStyle}>
                {formatDate(finalizedAt, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY)}
              </Text>
            </VStack>
          </Grid>
          {!!memoLines && (
            <VStack space="4px">
              <Text variant="body" color="secondary">
                {t('invoice.memo')}
              </Text>
              <MemoWrapper>
                <Text variant="hint">{memoLines}</Text>
              </MemoWrapper>
            </VStack>
          )}
        </>
      ) : (
        <>
          <Grid data-test="billing-info" columns="120px 1fr" gap="4px 32px">
            <Text variant="body" color="secondary">
              {t('invoice.billFrom')}
            </Text>
            <VStack style={{ overflow: 'hidden' }}>
              <Text variant="body" style={labelStyle}>
                {name}
              </Text>
              <Text variant="hint">{email}</Text>
            </VStack>
            <Text variant="body" color="secondary">
              {t('invoice.billTo')}
            </Text>
            <VStack style={{ overflow: 'hidden' }}>
              <Text variant="body" style={labelStyle}>
                {getBillToTitle(customerSnapshot)}
              </Text>
              <Text variant="hint">{getBillToSubTitle(customerSnapshot)}</Text>
            </VStack>
            <Text variant="body" color="secondary">
              {t('invoice.invoiceDate')}
            </Text>
            <Text variant="body" style={labelStyle}>
              {formatDate(finalizedAt, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY)}
            </Text>
          </Grid>
          {!!memoLines && (
            <VStack space="4px">
              <Text variant="body" color="secondary">
                {t('invoice.memo')}
              </Text>
              <MemoWrapper>
                <Text variant="hint">{memoLines}</Text>
              </MemoWrapper>
            </VStack>
          )}
        </>
      )}
    </>
  );
};

export default BillingInfo;
